import { useAuth } from "@/contexts/AuthContext";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ArrowRight, LineChart, ShieldCheck, Sparkles, Sprout,
  Wallet, Receipt, Landmark, CreditCard, ScrollText, CalendarDays,
  Target, TrendingUp, PieChart, BarChart3, Bell, Brain,
  Bot, Sunrise, Flame, Users, Music2, Globe2,
} from "lucide-react";
import AuthModal from "@/components/AuthModal";
import LandingLanguagePicker from "@/components/LandingLanguagePicker";

export default function Landing() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState("login");
  const openAuth = (mode = "login") => { setAuthMode(mode); setAuthOpen(true); };

  // Capture ?ref=... and ?join=... on landing so they survive the Google OAuth round-trip
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref");
    const join = params.get("join");
    try {
      if (ref) localStorage.setItem("pfo_ref", ref);
      if (join) localStorage.setItem("pfo_join", join.toUpperCase());
    } catch (e) { console.warn("Landing: could not persist referral params", e); }
  }, []);

  return (
    <div className="min-h-screen bg-[var(--bg-primary)] text-[var(--text-primary)]">
      {/* Header */}
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-[var(--bg-primary)]/80 border-b border-[var(--border-default)]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-[var(--accent-primary)] flex items-center justify-center logo-breathe">
              <Sprout className="text-white" size={18} strokeWidth={2} />
            </div>
            <div>
              <div className="font-mono text-[10px] tracking-[0.3em] text-[var(--text-secondary)] uppercase">Personal</div>
              <div className="font-medium tracking-tight leading-none" style={{fontFamily:'var(--font-heading)'}}>Finance Optimizer</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a href="#features" className="hidden md:inline text-sm text-[var(--text-secondary)] hover:text-[var(--accent-primary)]">{t("landing.header_features")}</a>
            <a href="#how" className="hidden md:inline text-sm text-[var(--text-secondary)] hover:text-[var(--accent-primary)]">{t("landing.header_how")}</a>
            <LandingLanguagePicker testId="landing-header-lang" />
            {user ? (
              <a href="/dashboard" className="btn-primary" data-testid="header-go-dashboard-btn">{t("landing.header_open_app")} <ArrowRight size={16} /></a>
            ) : (
              <button className="btn-primary" onClick={() => openAuth("login")} data-testid="header-signin-btn">
                {t("landing.header_signin")} <ArrowRight size={16} />
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden bg-warm-ambient">
        <div
          className="absolute inset-0 -z-10 opacity-40"
          style={{
            backgroundImage: "linear-gradient(180deg, rgba(253,252,251,0.85) 0%, rgba(253,252,251,0.98) 60%, #FDFCFB 100%), url('https://images.pexels.com/photos/9564681/pexels-photo-9564681.jpeg')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-28 lg:pt-28 lg:pb-36 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7 animate-fadeup">
            <SproutMascot />
            <div className="label-eyebrow mb-6">{t("landing.eyebrow")}</div>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-medium leading-[1.02] tracking-tighter">
              {t("landing.headline")}
            </h1>
            <p className="mt-7 text-lg text-[var(--text-secondary)] max-w-xl leading-relaxed">
              {t("landing.subheadline")}
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-3">
              <button className="btn-primary" onClick={() => openAuth("signup")} data-testid="hero-signin-btn">
                {t("landing.cta_get_started")} <ArrowRight size={16} />
              </button>
              <a href="#features" className="btn-ghost" data-testid="hero-explore-btn">{t("landing.header_how")}</a>
            </div>
            <div className="mt-10 flex items-center gap-6 text-xs uppercase tracking-[0.18em] text-[var(--text-muted)]">
              <span className="flex items-center gap-1.5"><ShieldCheck size={14}/> Bank-grade privacy</span>
              <span className="flex items-center gap-1.5"><Sparkles size={14}/> AI-powered</span>
              <span className="flex items-center gap-1.5"><LineChart size={14}/> Multi-currency</span>
            </div>
          </div>

          {/* 12 modules — system overview grid */}
          <div className="lg:col-span-5 animate-fadeup">
            <div
              className="card-flat p-6 shadow-lift tilt relative overflow-hidden"
              data-testid="hero-modules-card"
            >
              {/* subtle radial accent in the corner */}
              <div
                aria-hidden="true"
                className="absolute -top-14 -right-10 h-44 w-44 rounded-full pointer-events-none"
                style={{
                  background:
                    "radial-gradient(circle, rgba(224,158,80,0.18) 0%, rgba(224,158,80,0) 70%)",
                }}
              />

              <div className="relative flex items-center justify-between">
                <div className="label-eyebrow">Twelve modules</div>
                <span className="badge badge-success">One picture</span>
              </div>

              <h3
                className="mt-3 text-xl md:text-2xl font-medium tracking-tight leading-snug"
                style={{ fontFamily: "var(--font-heading)" }}
              >
                Every corner of your money life
              </h3>

              <div className="mt-5 grid grid-cols-3 gap-2.5">
                <ModuleTile icon={<Wallet size={18} />}     label="Income"        tone="green"  testid="mod-income"    />
                <ModuleTile icon={<Receipt size={18} />}    label="Expenses"      tone="rose"   testid="mod-expense"   />
                <ModuleTile icon={<Landmark size={18} />}   label="Loans & EMIs"  tone="olive"  testid="mod-loans"     />
                <ModuleTile icon={<CreditCard size={18} />} label="Subscriptions" tone="amber"  testid="mod-subs"      />
                <ModuleTile icon={<ScrollText size={18} />} label="Bills"         tone="olive"  testid="mod-bills"     />
                <ModuleTile icon={<CalendarDays size={18}/>}label="Yearly plans"  tone="rose"   testid="mod-yearly"    />
                <ModuleTile icon={<Target size={18} />}     label="Goals"         tone="green"  testid="mod-goals"     />
                <ModuleTile icon={<TrendingUp size={18} />} label="Investments"   tone="amber"  testid="mod-invest"    />
                <ModuleTile icon={<PieChart size={18} />}   label="Budgets"       tone="olive"  testid="mod-budget"    />
                <ModuleTile icon={<BarChart3 size={18} />}  label="Reports"       tone="green"  testid="mod-reports"   />
                <ModuleTile icon={<Bell size={18} />}       label="Reminders"     tone="amber"  testid="mod-reminders" />
                <ModuleTile icon={<Brain size={18} />}      label="AI Advisor"    tone="hero"   testid="mod-ai"        />
              </div>

              <div className="mt-5 text-xs text-[var(--text-secondary)] flex items-center gap-1.5">
                <Sparkles size={12} className="text-[var(--accent-primary)]" />
                All twelve talk to each other — one number, one plan.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features — unique differentiators */}
      <section id="features" className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="max-w-2xl">
          <div className="label-eyebrow mb-3">What makes it different</div>
          <h2 className="text-4xl md:text-5xl tracking-tighter">AI that actually acts on your money.</h2>
          <p className="mt-4 text-[var(--text-secondary)]">
            The twelve modules are the foundation. These six systems on top are what turn tracking into results.
          </p>
        </div>

        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-5 stagger">
          <Feature icon={<Bot/>}     title="AI Financial Advisor" body="Claude Sonnet 4.5 with full context of your income, expenses and goals. Ask anything." />
          <Feature icon={<Sunrise/>} title="Daily AI Nudge"       body="One specific, actionable insight at the top of your dashboard every morning." />
          <Feature icon={<Flame/>}   title="Savings Streaks"      body="Gamified consistency. Milestones, streak-saves and referral tokens to protect your run." />
          <Feature icon={<Users/>}   title="Money Circles"        body="Private accountability groups with leaderboards, weekly digests and Circle-of-the-Week." />
          <Feature icon={<Music2/>}  title="Monthly Wrap-up"      body="Spotify-style month recap with your top wins, top spends and a shareable PNG." />
          <Feature icon={<Globe2/>}  title="35+ currencies"       body="Full Arab-League support — SAR, AED, KWD, EGP, JOD, QAR, MAD, OMR and more." />
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="bg-[var(--bg-secondary)] border-y border-[var(--border-default)]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="label-eyebrow mb-3">The loop</div>
            <h2 className="text-4xl tracking-tighter">Monitor → Analyze → Optimize → Improve.</h2>
            <p className="mt-4 text-[var(--text-secondary)]">
              A continuous financial-health improvement loop. It gets smarter the more you use it.
            </p>
          </div>
          <div className="lg:col-span-8 grid sm:grid-cols-2 gap-4">
            {[
              ["01 · Onboard",    "Sign in with Google or email and set your currency & preferences."],
              ["02 · Track",      "Add income, expenses, loans, bills and investments in seconds."],
              ["03 · Understand", "Get a Financial Health Score, forecasts and category insights."],
              ["04 · Optimize",   "AI-powered recommendations sorted by monthly & yearly savings."],
              ["05 · Act",        "Follow the step-by-step action plan tailored to you."],
              ["06 · Achieve",    "Watch your net worth climb and stress level drop."],
            ].map(([step, body]) => (
              <div key={step} className="card-flat p-5">
                <div className="font-mono text-xs tracking-widest text-[var(--text-secondary)]">{step}</div>
                <div className="mt-2 text-sm leading-relaxed text-[var(--text-primary)]">{body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 text-center">
        <h2 className="text-4xl md:text-5xl tracking-tighter">Ready to take back control?</h2>
        <p className="mt-4 text-[var(--text-secondary)] max-w-xl mx-auto">
          Free while you evaluate. No card required. Your data stays private and encrypted.
        </p>
        <button className="btn-primary mt-8 mx-auto" onClick={() => openAuth("signup")} data-testid="cta-signin-btn">
          Get started <ArrowRight size={16} />
        </button>
      </section>

      <footer className="border-t border-[var(--border-default)] py-8 text-center text-xs text-[var(--text-muted)]">
        © {new Date().getFullYear()} Personal Finance Optimizer · Built with care.
      </footer>

      <AuthModal open={authOpen} initialMode={authMode} onClose={() => setAuthOpen(false)} />
    </div>
  );
}

function SproutMascot() {
  return (
    <div className="mb-5 inline-flex items-end gap-3" data-testid="hero-sprout-mascot" aria-hidden="true">
      <div className="mascot-sway">
        <div className="mascot-breathe">
          <div
            className="clay-icon"
            style={{
              width: 68, height: 68,
              background: "linear-gradient(155deg, #6BA37F 0%, #2D5A40 100%)",
              color: "#FDFCFB",
              borderRadius: 22,
            }}
          >
            <Sprout size={34} strokeWidth={2.1} />
          </div>
        </div>
      </div>
      <div className="pb-1 hidden sm:flex flex-col leading-tight">
        <span className="font-mono text-[10px] tracking-[0.28em] uppercase text-[var(--text-muted)]">
          Meet Sprout
        </span>
        <span className="text-xs text-[var(--text-secondary)] max-w-[16rem]">
          Your quiet money companion — grows with every consistent month.
        </span>
      </div>
    </div>
  );
}

function Feature({ icon, title, body }) {
  return (
    <div className="card-surface p-6">
      <div className="h-10 w-10 rounded-lg bg-[var(--bg-accent)] flex items-center justify-center text-[var(--accent-primary)]">
        {icon}
      </div>
      <div className="mt-4 font-medium text-lg" style={{fontFamily:'var(--font-heading)'}}>{title}</div>
      <div className="mt-1.5 text-sm text-[var(--text-secondary)] leading-relaxed">{body}</div>
    </div>
  );
}

function ModuleTile({ icon, label, tone = "green", testid }) {
  // Palette rotates through 4 tones so the grid feels varied without going rainbow.
  // Each tone has a soft base for the tile card + a bolder clay gradient for the icon.
  const styles = {
    green: {
      tile:      "rgba(45,90,64,0.04)",
      clay:      "linear-gradient(155deg, #6BA37F 0%, #2D5A40 100%)",
      iconColor: "#FDFCFB",
    },
    olive: {
      tile:      "rgba(87,106,94,0.05)",
      clay:      "linear-gradient(155deg, #8FA697 0%, #4A5D51 100%)",
      iconColor: "#FDFCFB",
    },
    amber: {
      tile:      "rgba(224,158,80,0.06)",
      clay:      "linear-gradient(155deg, #F5C384 0%, #C88940 100%)",
      iconColor: "#FDFCFB",
    },
    rose:  {
      tile:      "rgba(158,84,74,0.05)",
      clay:      "linear-gradient(155deg, #C68F86 0%, #8B4A44 100%)",
      iconColor: "#FDFCFB",
    },
    hero:  {
      tile:      "linear-gradient(135deg, rgba(45,90,64,0.06) 0%, rgba(224,158,80,0.09) 100%)",
      clay:      "linear-gradient(155deg, #E09E50 0%, #2D5A40 100%)",
      iconColor: "#FDFCFB",
    },
  };
  const s = styles[tone] || styles.green;
  return (
    <div
      className="clay-tile rounded-xl border border-[var(--border-default)] p-3 flex flex-col items-center justify-center gap-2 text-center hover:border-[var(--accent-primary)] transition-colors"
      style={{ background: s.tile }}
      data-testid={testid}
    >
      <div
        className="clay-icon h-11 w-11"
        style={{ background: s.clay, color: s.iconColor }}
      >
        {icon}
      </div>
      <div className="text-[11px] font-medium leading-tight tracking-tight text-[var(--text-primary)]">
        {label}
      </div>
    </div>
  );
}
